import { Account } from '@brenger/api-client';
import { getIdFromIri } from '@brenger/utils';
import * as Sentry from '@sentry/react';
import { store } from '../../..';
import { RootState } from '../../../types';
import { logError, pushToDataLayer } from '../../../utils/basics';
import { coreClient, getIsProduction } from '../../../utils/request';
import { setSentryContext } from '../../../utils/sentry';
import { runSandboxLogin } from '../../ApiSettings/utils/runSandboxLogin';
import { getAccountHasBusinessFlow, actions as userActions } from '../ducks';
/**
 * Loads the current user profile (same behavior as the former retrieveCurrentUser saga):
 * isUserLoggedIn, optional sandbox re-auth in non-production (same steps as LOGIN_SANDBOX_START saga),
 * retrieveCurrentUser API, setUser, BRENGER_USER_UPDATED, optional BRENGER_OPEN_CHAT for business flow.
 *
 * Uses `store` from `src/index` via dynamic import to avoid circular module init with sagas.
 * Once `signal` is aborted, nothing more is dispatched or reported (as a cancelled saga did).
 */
export async function retrieveCurrentUser(signal?: AbortSignal): Promise<void> {
  const { dispatch, getState } = store;

  let isLoggedIn = false;
  try {
    await coreClient.users.isUserLoggedIn();
    isLoggedIn = true;
  } catch (e) {
    logError(e);
  }
  if (signal?.aborted) return;

  try {
    if (!isLoggedIn && !getIsProduction()) {
      try {
        await runSandboxLogin(signal);
      } catch {
        throw new Error('Sandbox auth failed');
      }
      if (signal?.aborted) return;
      isLoggedIn = true;
    }

    if (!isLoggedIn) {
      throw new Error('We are not authenticated');
    }

    const loggedInUser = await coreClient.users.retrieveCurrentUser();
    if (signal?.aborted) return;
    dispatch(userActions.setUser(loggedInUser));

    setSentryContext('user', {
      id: loggedInUser['@id'],
      hasBusinessFlow: (loggedInUser?.account as Account | undefined)?.has_business_flow,
    });
    Sentry.setTag('logged_in_session', true);

    const event = new CustomEvent('BRENGER_USER_UPDATED', {
      detail: {
        result: loggedInUser,
      },
    });
    document.dispatchEvent(event);

    if (getAccountHasBusinessFlow(getState() as RootState)) {
      document.dispatchEvent(
        new CustomEvent('BRENGER_OPEN_CHAT', {
          detail: {
            open: false,
            tags: ['logged_in_business_flow_customer'],
          },
        })
      );
    }
    pushToDataLayer({ event: 'user info', loggedIn: true, id: getIdFromIri(loggedInUser['@id']) });
  } catch (e) {
    if (signal?.aborted) return;
    logError(e);
    dispatch(userActions.setUser(null));
    pushToDataLayer({ event: 'user info', loggedIn: false });
    Sentry.setTag('logged_in_session', false);
    setSentryContext('user', {
      id: null,
      hasBusinessFlow: false,
    });
  }
}
